import React from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { LinkPresentationProps } from "@insite/mobius/Link";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import LazyImage from "@insite/mobius/LazyImage";
import { css } from "styled-components";
import BasicList from "@WhiteCap/Components/BasicList";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Divider from "@WhiteCap/Components/Basic/Divider";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import GenrateUrl from "@WhiteCap/Components/Basic/GenrateUrl";
import Zone from "@insite/client-framework/Components/Zone";

const enum fields {
    title = "title",
    desc = "desc",
    categories = "categories",
}

export interface SuspensionSystemCategoryModel {
    fields: {
        name: string;
        image: string;
        linkText: string;
        link: LinkFieldValue;
        openInNewWindow: boolean;
    };
}
export interface FieldsPropsSuspensionSystem {
    [fields.title]: string;
    [fields.desc]: string;
    [fields.categories]: SuspensionSystemCategoryModel[];
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.desc]: string;
        [fields.categories]: SuspensionSystemCategoryModel[];
    };
}

export interface SuspensionSystemStyles {
    container?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    mainGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyProps;
    productContainer?: InjectableCss;
    imageContainer?: InjectableCss;
    contentGridItem?: GridItemProps;
    link?: LinkPresentationProps;
    additionalInfoWrapper?: InjectableCss;
}

const suspensionSystemStyle: SuspensionSystemStyles = {
    mainGridContainer: {
        css: css`
            margin-top: 50px;
        `,
    },
    mainGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            padding: 0;
            padding-top: 20px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    heading: {
        css: css`
            font-size: 18px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            line-height: 1.5;
            text-align: center;
            width: 100%;
        `,
    },
    contentGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    productContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
        `,
    },
    imageContainer: {
        css: css`
            min-height: 175px;
        `,
    },

    link: {
        color: WhitecapColors.orangeLink,
        css: css`
            font-size: 14px;
            &:hover {
                text-decoration: underline;
                ${TypographyStyle} {
                    color: ${WhitecapColors.lightBlack};
                }
            }
        `,
    },
    additionalInfoWrapper: {
        css: css`
            min-width: 300px;
        `,
    },
};

const styles = suspensionSystemStyle;

const SafetySystem = ({ fields, id }: OwnProps) => {
    return (
        <StyledWrapper {...styles.container}>
            <BasicList fields={fields} />
            <GridContainer {...styles.mainGridContainer}>
                {fields.categories.map(item => (
                    <GridItem {...styles.mainGridItem} key={item.fields.name}>
                        <GridContainer id={`${item.fields.name.replace(" ", "-")}`}>
                            <GridItem {...styles.headingGridItem}>
                                <Typography variant="h3" {...styles.heading}>
                                    {item.fields.name}
                                </Typography>
                                <Divider borderTop={`1px solid ${WhitecapColors.borderGray}`} />
                            </GridItem>

                            <GridItem {...styles.contentGridItem}>
                                <StyledWrapper {...styles.productContainer}>
                                    <StyledWrapper {...styles.imageContainer}>
                                        <LazyImage src={item.fields.image} />
                                    </StyledWrapper>

                                    <GenrateUrl
                                        link={item.fields.link}
                                        style={styles.link}
                                        openInNewTab={item.fields.openInNewWindow}
                                    >
                                        {item.fields.linkText}
                                    </GenrateUrl>
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                ))}
                <StyledWrapper {...styles.additionalInfoWrapper}>
                    <Zone contentId={id} zoneName="BrowseSuspensionSystem" />
                </StyledWrapper>
            </GridContainer>
        </StyledWrapper>
    );
};

const SafetySystemModule: WidgetModule = {
    component: SafetySystem,
    definition: {
        displayName: "Categories and Product List",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "desc",
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.categories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Category Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "image",
                        displayName: "Icon Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },

                    {
                        name: "linkText",
                        displayName: "Link Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                    },
                ],
            },
        ],

        group: "Common",
    },
};

export default SafetySystemModule;
